import DocumentManagement from '@/services/documentmanagement.service'

// Threshold: files whose base64 string exceeds this are chunked (~70 MB binary).
// Base64 inflates by ~33%, so 70 MB binary → ~93 M base64 chars.
const CHUNK_THRESHOLD_B64 = Math.floor(70 * 1024 * 1024 * 4 / 3); // ~93 M chars

// Each chunk is 30 MB of raw binary bytes.
const CHUNK_SIZE_BYTES = 30 * 1024 * 1024; // 30 MB

// Encode a Uint8Array slice to a base64 string without stack-overflow risk.
// String.fromCharCode.apply batched in 32 KB pieces avoids exceeding call-stack limits.
function uint8ArrayToBase64(bytes) {
    const BATCH = 0x8000; // 32 768
    let binary = '';
    for (let i = 0; i < bytes.length; i += BATCH) {
        binary += String.fromCharCode.apply(null, bytes.subarray(i, i + BATCH));
    }
    return btoa(binary);
}

export default {
    methods: {
        generateUploadId() {
            return 'upload_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
        },

        // isNewField / contentsField let callers match their own file schema.
        // Default: LoadDocument/PatientDocument schema (IsNew, contents).
        // MdNotesInFiles schema uses (new, base64) — pass { isNewField:'new', contentsField:'base64' }.
        async preUploadLargeFiles(files, thresholdB64 = CHUNK_THRESHOLD_B64, { isNewField = 'IsNew', contentsField = 'contents' } = {}) {
            for (const file of files) {
                if (!file[isNewField] || !file[contentsField]) continue;
                if (file[contentsField].length <= thresholdB64) continue;

                const uploadId = this.generateUploadId();

                // Decode the full base64 string to binary once, then slice binary chunks.
                // Slicing the base64 string directly would send base64-text bytes to the
                // server instead of the original file bytes, corrupting the assembled file.
                const binaryString = atob(file[contentsField]);
                const bytes = new Uint8Array(binaryString.length);
                for (let i = 0; i < binaryString.length; i++) {
                    bytes[i] = binaryString.charCodeAt(i);
                }

                const totalChunks = Math.ceil(bytes.length / CHUNK_SIZE_BYTES);

                for (let i = 0; i < totalChunks; i++) {
                    const start = i * CHUNK_SIZE_BYTES;
                    const chunkData = uint8ArrayToBase64(bytes.slice(start, start + CHUNK_SIZE_BYTES));
                    try {
                        await DocumentManagement.AddChunk({ uploadId, chunkIndex: i, totalChunks, data: chunkData });
                    } catch (err) {
                        this.$emit('load', false);
                        this.showMessage(
                            this.$tc('Msg.Error'),
                            `${this.$t('Msg.ThereWereSomeError')} (${file.fileName || file.name} chunk ${i + 1}/${totalChunks})`,
                            'error'
                        );
                        err.isHandled = true;
                        throw err;
                    }
                }

                file[contentsField] = null;
                file.uploadId = uploadId;
                file.isChunked = true;
            }
        }
    }
}
